import React, { Fragment, useEffect } from 'react';
import T from 'prop-types';
import { Keyboard, Linking } from 'react-native';
import isEmpty from 'lodash/isEmpty';

import { FLEX_DRAFT_DRAWER_DICTIONARY } from '@dmi/shared/redux/Payments/FlexDraft/constants';
import {
  PAYMENT_SOURCES_FIRST_ACCOUNT,
  PAYMENT_SOURCES_SECOND_ACCOUNT,
} from '@dmi/shared/redux/Payments/Sources/constants';
import { validateSourceFormInputs } from '@dmi/shared/redux/Payments/Sources/helpers';
import { interpolate } from '@dmi/shared/utils/globalHelpers';

import {
  ConditionalRender,
  ErrorSuccessBanner,
  FormattedMessage,
  KeyboardAvoidingScrollView,
} from '../../../base_ui';
import { SecondaryHeader } from '../../../ScreenHeaders';
import ScreenReaderNotifications from '../../../ScreenReaderNotifications';
import { InfoSection } from '../../sharedComponents';
import HelperImg from '../../sharedComponents/PaymentSource/1.0-Check-Example-327x84.png';
import { checkFormErrors, checkIncompleteForm } from '../../sharedComponents/PaymentSource/helpers';
import AccountForm from './AccountForm';
import SecondAccountAdd from './SecondAccountAdd';
import SecondAccountRemove from './SecondAccountRemove';
import {
  BoldText,
  FormContainer,
  ImageContainer,
  InfoSectionContainer,
  StyledImage,
  StyledLargeProgressButton,
  StyledPhoneLinkText,
  StyledText,
  TextWrapper,
} from './styledComponents';

const EditSourcesComponent = ({
  accountInfoExisting: {
    [PAYMENT_SOURCES_FIRST_ACCOUNT]: firstAccountInfoExisting,
    [PAYMENT_SOURCES_SECOND_ACCOUNT]: secondAccountInfoExisting,
  },
  accountInfoNew: {
    [PAYMENT_SOURCES_FIRST_ACCOUNT]: firstAccountInfoNew,
    [PAYMENT_SOURCES_SECOND_ACCOUNT]: secondAccountInfoNew,
  },
  bannerError,
  customerServicePhoneNumber,
  dispatchClearNewInfo,
  dispatchResetFormState,
  dispatchSetActiveAccountForm,
  dispatchSetInput,
  dispatchSetInputError,
  dispatchSetIsSecondAccountFormEnabled,
  dispatchSetUseExisting,
  dispatchValidateRouting,
  dispatchValidateRoutingSuccess,
  formState: {
    activeAccountForm,
    formErrors: {
      [PAYMENT_SOURCES_FIRST_ACCOUNT]: firstAccountFormErrors,
      [PAYMENT_SOURCES_SECOND_ACCOUNT]: secondAccountFormErrors,
    },
    formValues: {
      [PAYMENT_SOURCES_FIRST_ACCOUNT]: firstAccountFormValues,
      [PAYMENT_SOURCES_SECOND_ACCOUNT]: secondAccountFormValues,
    },
  },
  infoSectionData,
  isSecondAccountFormEnabled,
  navigation,
  notifications,
  shouldRenderValidateButton,
  useExistingInfo: {
    [PAYMENT_SOURCES_FIRST_ACCOUNT]: firstAccountUseExistingInfo,
    [PAYMENT_SOURCES_SECOND_ACCOUNT]: secondAccountUseExistingInfo,
  },
  useNewInfo: {
    [PAYMENT_SOURCES_FIRST_ACCOUNT]: firstAccountUseNewInfo,
    [PAYMENT_SOURCES_SECOND_ACCOUNT]: secondAccountUseNewInfo,
  },
  validateRoutingError,
  validateRoutingLoading,
  validateRoutingSuccess,
}) => {
  const { buttonLabel, headerText, paragraph } = FLEX_DRAFT_DRAWER_DICTIONARY.sources;

  useEffect(() => {
    if (secondAccountUseExistingInfo || secondAccountUseNewInfo) {
      dispatchSetIsSecondAccountFormEnabled(true);
    }
  }, [
    dispatchSetIsSecondAccountFormEnabled,
    secondAccountUseExistingInfo,
    secondAccountUseNewInfo,
  ]);

  useEffect(() => () => {
    dispatchValidateRoutingSuccess(false);
    dispatchResetFormState({ form: PAYMENT_SOURCES_FIRST_ACCOUNT });
    dispatchResetFormState({ form: PAYMENT_SOURCES_SECOND_ACCOUNT });
    if (!secondAccountUseExistingInfo && !secondAccountUseNewInfo) {
      dispatchSetIsSecondAccountFormEnabled(false);
    }
  }, [
    dispatchResetFormState,
    dispatchSetIsSecondAccountFormEnabled,
    dispatchValidateRoutingSuccess,
    secondAccountUseExistingInfo,
    secondAccountUseNewInfo,
  ]);

  const handleDispatchClearNewInfo = (accountForm) => {
    handleSetActiveAccountForm(accountForm);
    dispatchClearNewInfo();
  };

  const handleDispatchSetInputFirstAccount = (payload) => {
    handleSetActiveAccountForm(PAYMENT_SOURCES_FIRST_ACCOUNT);
    dispatchSetInput(payload);
  };

  const handleDispatchSetInputSecondAccount = (payload) => {
    handleSetActiveAccountForm(PAYMENT_SOURCES_SECOND_ACCOUNT);
    dispatchSetInput(payload);
  };

  const handleDispatchSetUseExisting = ({ accountForm, value }) => {
    handleSetActiveAccountForm(accountForm);
    dispatchSetUseExisting(value, 'flexdraft');
  };

  const handleOnClickSecondAccountRemove = () => {
    dispatchSetIsSecondAccountFormEnabled(false);
    dispatchResetFormState({ form: PAYMENT_SOURCES_SECOND_ACCOUNT });
    handleDispatchClearNewInfo(PAYMENT_SOURCES_SECOND_ACCOUNT);
    handleDispatchSetUseExisting({
      accountForm: PAYMENT_SOURCES_SECOND_ACCOUNT,
      value: false,
    });
  };

  const handleSetActiveAccountForm = (accountForm) => {
    if (activeAccountForm !== accountForm) {
      dispatchSetActiveAccountForm({ form: accountForm });
    }
  };

  const handleValidatePress = () => {
    const payload = {};
    let hasInputErrors = false;

    // Validate first account if not previously validated
    if (!firstAccountUseExistingInfo && !firstAccountUseNewInfo) {
      const firstFormHasInputErrors = validateSourceFormInputs({
        activeAccountForm: PAYMENT_SOURCES_FIRST_ACCOUNT,
        dispatchSetInputError,
        formValues: firstAccountFormValues,
      });
      if (!firstFormHasInputErrors) {
        payload.firstAccount = firstAccountFormValues;
      } else {
        hasInputErrors = true;
      }
    }

    // Validate second account if not previously validated and if enabled
    if (!secondAccountUseExistingInfo && !secondAccountUseNewInfo && isSecondAccountFormEnabled) {
      const secondFormHasInputErrors = validateSourceFormInputs({
        activeAccountForm: PAYMENT_SOURCES_SECOND_ACCOUNT,
        dispatchSetInputError,
        formValues: secondAccountFormValues,
      });
      if (!secondFormHasInputErrors) {
        payload.secondAccount = secondAccountFormValues;
      } else {
        hasInputErrors = true;
      }
    }

    // If no input errors then validate routing number(s)
    if (!isEmpty(payload) && !hasInputErrors) {
      dispatchValidateRouting(payload);
      Keyboard.dismiss();
    }
  };

  const isValidateButtonDisabled = isSecondAccountFormEnabled ?
    (
      validateRoutingLoading ||
      checkIncompleteForm(firstAccountFormValues) ||
      checkIncompleteForm(secondAccountFormValues) ||
      checkFormErrors(firstAccountFormErrors) ||
      checkFormErrors(secondAccountFormErrors)
    ) : (
      validateRoutingLoading ||
      checkIncompleteForm(firstAccountFormValues) ||
      checkFormErrors(firstAccountFormErrors)
    );

  return (
    <Fragment>
      <ScreenReaderNotifications notifications={notifications} />
      <SecondaryHeader
        handleBack={navigation.goBack}
        title={headerText}
      />
      <KeyboardAvoidingScrollView scrollViewProps={{ showsVerticalScrollIndicator: false }}>
        <ErrorSuccessBanner
          error={bannerError ?
            interpolate(bannerError, { customerServicePhoneNumber }) : bannerError}
        />
        <ImageContainer>
          <StyledImage aria-label="check example" source={HelperImg} />
        </ImageContainer>
        <FormContainer>
          <TextWrapper>
            {paragraph.map((text) => (
              <FormattedMessage
                key={text}
                defaultMessage={interpolate(text, { customerServicePhoneNumber })}
                // eslint-disable-next-line react/prop-types
                values={{
                  // eslint-disable-next-line react/prop-types
                  a: ({ msg }) => (
                    <StyledPhoneLinkText
                      // eslint-disable-next-line react/prop-types
                      onPress={() => Linking.openURL(`tel://${msg.replace(/\+|-/g, '')}`)}
                    >
                      {msg}
                    </StyledPhoneLinkText>
                  ),
                  // eslint-disable-next-line react/prop-types
                  b: ({ msg }) => <BoldText>{msg}</BoldText>,
                }}
                WrapperComponent={StyledText}
              />
            ))}
          </TextWrapper>
          <AccountForm
            accountForm={PAYMENT_SOURCES_FIRST_ACCOUNT}
            formErrors={firstAccountFormErrors}
            formValues={firstAccountFormValues}
            handleDispatchClearNewInfo={handleDispatchClearNewInfo}
            handleDispatchSetInput={handleDispatchSetInputFirstAccount}
            handleDispatchSetUseExisting={handleDispatchSetUseExisting}
            handleValidatePress={handleValidatePress}
            infoExisting={firstAccountInfoExisting}
            infoNew={firstAccountInfoNew}
            isValidateButtonDisabled={isValidateButtonDisabled}
            useExistingInfo={firstAccountUseExistingInfo}
            useNewInfo={firstAccountUseNewInfo}
          />
          <ConditionalRender
            Component={SecondAccountRemove}
            FallbackComponent={SecondAccountAdd}
            propsToPassDown={{
              dispatchSetIsSecondAccountFormEnabled,
              handleOnClickSecondAccountRemove,
            }}
            shouldRender={isSecondAccountFormEnabled}
          />
          <ConditionalRender
            Component={(
              <AccountForm
                accountForm={PAYMENT_SOURCES_SECOND_ACCOUNT}
                formErrors={secondAccountFormErrors}
                formValues={secondAccountFormValues}
                handleDispatchClearNewInfo={handleDispatchClearNewInfo}
                handleDispatchSetInput={handleDispatchSetInputSecondAccount}
                handleDispatchSetUseExisting={handleDispatchSetUseExisting}
                handleValidatePress={handleValidatePress}
                infoExisting={secondAccountInfoExisting}
                infoNew={secondAccountInfoNew}
                isValidateButtonDisabled={isValidateButtonDisabled}
                useExistingInfo={secondAccountUseExistingInfo}
                useNewInfo={secondAccountUseNewInfo}
              />
            )}
            shouldRender={isSecondAccountFormEnabled}
          />
        </FormContainer>

        <InfoSectionContainer>
          <InfoSection
            data={infoSectionData}
          />
        </InfoSectionContainer>
        <ConditionalRender
          Component={StyledLargeProgressButton}
          propsToPassDown={{
            $hasMarginTop: !isSecondAccountFormEnabled,
            disabled: isValidateButtonDisabled,
            error: validateRoutingError,
            label: buttonLabel,
            loading: validateRoutingLoading,
            onPress: handleValidatePress,
            success: validateRoutingSuccess,
          }}
          shouldRender={shouldRenderValidateButton}
        />
      </KeyboardAvoidingScrollView>
    </Fragment>
  );
};

EditSourcesComponent.propTypes = {
  accountInfoExisting: T.shape({
    [PAYMENT_SOURCES_FIRST_ACCOUNT]: T.object.isRequired,
    [PAYMENT_SOURCES_SECOND_ACCOUNT]: T.object.isRequired,
  }),
  accountInfoNew: T.shape({
    [PAYMENT_SOURCES_FIRST_ACCOUNT]: T.object.isRequired,
    [PAYMENT_SOURCES_SECOND_ACCOUNT]: T.object.isRequired,
  }),
  bannerError: T.oneOfType([T.bool, T.string]).isRequired,
  customerServicePhoneNumber: T.string.isRequired,
  dispatchClearNewInfo: T.func.isRequired,
  dispatchResetFormState: T.func.isRequired,
  dispatchSetActiveAccountForm: T.func.isRequired,
  dispatchSetInput: T.func.isRequired,
  dispatchSetInputError: T.func.isRequired,
  dispatchSetIsSecondAccountFormEnabled: T.func.isRequired,
  dispatchSetUseExisting: T.func.isRequired,
  dispatchValidateRouting: T.func.isRequired,
  dispatchValidateRoutingSuccess: T.func.isRequired,
  formState: T.shape({
    activeAccountForm: T.string.isRequired,
    formErrors: T.object.isRequired,
    formValues: T.object.isRequired,
  }).isRequired,
  infoSectionData: T.array.isRequired,
  isSecondAccountFormEnabled: T.bool.isRequired,
  navigation: T.object.isRequired,
  notifications: T.array.isRequired,
  shouldRenderValidateButton: T.bool.isRequired,
  useExistingInfo: T.shape({
    [PAYMENT_SOURCES_FIRST_ACCOUNT]: T.bool.isRequired,
    [PAYMENT_SOURCES_SECOND_ACCOUNT]: T.bool.isRequired,
  }),
  useNewInfo: T.shape({
    [PAYMENT_SOURCES_FIRST_ACCOUNT]: T.bool.isRequired,
    [PAYMENT_SOURCES_SECOND_ACCOUNT]: T.bool.isRequired,
  }),
  validateRoutingError: T.oneOfType([T.bool, T.string]).isRequired,
  validateRoutingLoading: T.bool.isRequired,
  validateRoutingSuccess: T.bool.isRequired,
};

export default EditSourcesComponent;
